import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, CheckCircle2, ShieldCheck, Users, Home, CreditCard, Bike, BrainCircuit, MapPin } from 'lucide-react';
import { Section } from './ui/Section';
const serviceSlides = [{
  icon: ShieldCheck,
  title: 'Physical Verification',
  description: 'Every property is inspected by our Simba Fleet ambassadors on branded bicycles',
  color: 'from-emerald-500 to-green-600'
}, {
  icon: BrainCircuit,
  title: 'AI Roommate Matching',
  description: 'Smart algorithm pairs you with compatible roommates based on lifestyle and preferences',
  color: 'from-brand-primary to-purple-600'
}, {
  icon: CreditCard,
  title: 'Secure Escrow Payments',
  description: 'Your money is protected until you verify the property and move in',
  color: 'from-blue-500 to-indigo-600'
}, {
  icon: MapPin,
  title: 'Safety Monitoring',
  description: 'Real-time tracking and 24/7 emergency response for student safety',
  color: 'from-orange-500 to-red-600'
}];
export function ExplainerVideo() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % serviceSlides.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPlaying]);
  const features = ['Physical verification by Simba Fleet', 'AI-powered roommate matching', 'Secure escrow payment system', '24/7 safety monitoring'];
  return <Section className="bg-gradient-to-br from-brand-dark via-brand-dark-secondary to-brand-dark text-white overflow-hidden relative" data-id="element-45">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: 'radial-gradient(circle, #b583c9 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-46"></div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-47">
        {/* Left: Text Content */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-48">
          <div className="inline-block px-3 py-1 bg-brand-primary/20 text-brand-primary rounded-full text-sm font-medium mb-6" data-id="element-49">
            How It Works
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-50">
            See CampusAparte <br data-id="element-51" />
            in <span className="text-brand-primary" data-id="element-52">Action</span>
          </h2>
          <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-53">
            Watch how we're revolutionizing student housing across Africa with
            verified properties, smart matching, and secure payments.
          </p>

          <ul className="space-y-4 mb-8" data-id="element-54">
            {features.map((feature, idx) => <motion.li key={idx} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.1
          }} className="flex items-center gap-3 text-lg" data-id="element-55">
                <div className="w-6 h-6 rounded-full bg-brand-primary/20 flex items-center justify-center flex-shrink-0" data-id="element-56">
                  <CheckCircle2 className="w-4 h-4 text-brand-primary" data-id="element-57" />
                </div>
                <span className="text-slate-200" data-id="element-58">{feature}</span>
              </motion.li>)}
          </ul>

          <div className="flex gap-4" data-id="element-59">
            <button className="px-6 py-3 bg-brand-primary hover:bg-brand-light text-white font-semibold rounded-lg transition-colors" data-id="element-60">
              Get Started
            </button>
            <button className="px-6 py-3 border border-brand-primary/30 hover:bg-brand-dark-secondary text-white font-semibold rounded-lg transition-colors" data-id="element-61">
              Learn More
            </button>
          </div>
        </motion.div>

        {/* Right: Custom Video Interface */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="relative" data-id="element-62">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-brand-primary/20 border border-brand-primary/20 bg-gradient-to-br from-brand-dark-secondary to-brand-dark" data-id="element-63">
            {/* Custom Video Player */}
            <div className="aspect-video relative" data-id="element-64">
              {!isPlaying ? <div className="relative w-full h-full flex items-center justify-center cursor-pointer group" onClick={() => setIsPlaying(true)} data-id="element-65">
                  {/* Background Image */}
                  <div className="absolute inset-0" data-id="element-66">
                    <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Students" className="w-full h-full object-cover opacity-40" data-id="element-67" />
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/40 to-brand-dark/80" data-id="element-68"></div>
                  </div>

                  {/* Animated Icons */}
                  <div className="absolute inset-0" data-id="element-69">
                    <motion.div animate={{
                  y: [0, -10, 0],
                  opacity: [0.5, 1, 0.5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} className="absolute top-1/4 left-1/4 w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center" data-id="element-70">
                      <ShieldCheck className="w-6 h-6 text-emerald-400" data-id="element-71" />
                    </motion.div>
                    <motion.div animate={{
                  y: [0, 10, 0],
                  opacity: [0.5, 1, 0.5]
                }} transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  delay: 0.5
                }} className="absolute top-1/3 right-1/4 w-12 h-12 bg-brand-primary/20 rounded-full flex items-center justify-center" data-id="element-72">
                      <Users className="w-6 h-6 text-brand-primary" data-id="element-73" />
                    </motion.div>
                    <motion.div animate={{
                  y: [0, -10, 0],
                  opacity: [0.5, 1, 0.5]
                }} transition={{
                  duration: 2.2,
                  repeat: Infinity,
                  delay: 1
                }} className="absolute bottom-1/4 left-1/3 w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center" data-id="element-74">
                      <Home className="w-6 h-6 text-blue-400" data-id="element-75" />
                    </motion.div>
                  </div>

                  {/* Play Button */}
                  <motion.button whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} className="relative z-10 w-20 h-20 bg-brand-primary hover:bg-brand-light rounded-full flex items-center justify-center shadow-lg shadow-brand-primary/50 transition-colors" data-id="element-76">
                    <Play className="w-8 h-8 text-white ml-1" fill="currentColor" data-id="element-77" />
                  </motion.button>

                  {/* Overlay Text */}
                  <div className="absolute bottom-6 left-6 right-6 z-10" data-id="element-78">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-79">
                      Watch Our Story
                    </h3>
                    <p className="text-slate-200 text-sm" data-id="element-80">
                      See how we're making student housing safer across Africa
                    </p>
                  </div>
                </div> : <div className="relative w-full h-full bg-gradient-to-br from-brand-dark-secondary to-brand-dark p-8 flex flex-col items-center justify-center" data-id="element-81">
                  {/* Animated Service Showcase */}
                  <AnimatePresence mode="wait" data-id="element-82">
                    <motion.div key={currentSlide} initial={{
                  opacity: 0,
                  scale: 0.8,
                  rotateY: -90
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  rotateY: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.8,
                  rotateY: 90
                }} transition={{
                  duration: 0.6
                }} className="text-center" data-id="element-83">
                      <motion.div className={`w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br ${serviceSlides[currentSlide].color} flex items-center justify-center shadow-2xl`} animate={{
                    rotate: [0, 360],
                    scale: [1, 1.1, 1]
                  }} transition={{
                    rotate: {
                      duration: 20,
                      repeat: Infinity,
                      ease: 'linear'
                    },
                    scale: {
                      duration: 2,
                      repeat: Infinity
                    }
                  }} data-id="element-84">
                        {(() => {
                      const Icon = serviceSlides[currentSlide].icon;
                      return <Icon className="w-12 h-12 text-white" data-id="element-85" />;
                    })()}
                      </motion.div>

                      <h3 className="text-3xl font-bold text-white mb-4" data-id="element-86">
                        {serviceSlides[currentSlide].title}
                      </h3>
                      <p className="text-lg text-slate-300 max-w-md mx-auto" data-id="element-87">
                        {serviceSlides[currentSlide].description}
                      </p>
                    </motion.div>
                  </AnimatePresence>

                  {/* Progress Dots */}
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2" data-id="element-88">
                    {serviceSlides.map((_, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2 h-2 rounded-full transition-all ${currentSlide === idx ? 'bg-brand-primary w-8' : 'bg-slate-600 hover:bg-slate-500'}`} data-id="element-89" />)}
                  </div>

                  {/* Close Button */}
                  <button onClick={() => setIsPlaying(false)} className="absolute top-4 right-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm font-medium transition-colors" data-id="element-90">
                    Close
                  </button>
                </div>}
            </div>

            {/* Stats Overlay */}
            {!isPlaying && <div className="absolute -bottom-6 left-6 right-6 bg-white rounded-xl shadow-xl p-4 grid grid-cols-3 gap-4" data-id="element-91">
                <div className="text-center" data-id="element-92">
                  <div className="text-2xl font-bold text-brand-dark" data-id="element-93">
                    5,000+
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-94">Students</div>
                </div>
                <div className="text-center border-x border-slate-200" data-id="element-95">
                  <div className="text-2xl font-bold text-brand-dark" data-id="element-96">850+</div>
                  <div className="text-xs text-slate-500" data-id="element-97">Properties</div>
                </div>
                <div className="text-center" data-id="element-98">
                  <div className="text-2xl font-bold text-brand-dark" data-id="element-99">120+</div>
                  <div className="text-xs text-slate-500" data-id="element-100">Ambassadors</div>
                </div>
              </div>}
          </div>
        </motion.div>
      </div>
    </Section>;
}